/**
 * StateSDG — /states/sdg
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStateContext } from "@/hooks/useStateContext";
import { RefreshCw } from "lucide-react";
import client from "@/api/client";
import { PageHeader, SectionCard } from "@/components/ds";

const SDG_PALETTE = {
  2: "#DDA63A", 3: "#4C9F38", 7: "#FCC30B", 8: "#A21942",
  9: "#FD6925", 11: "#FD9D24", 12: "#BF8B2E", 13: "#3F7E44"
};

export default function StateSDG() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { stateId, geography: geo, stateParams } = useStateContext();

  useEffect(() => {
    client.get(`/states/sdg${stateParams}`)
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [geo]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <RefreshCw size={18} className="animate-spin text-teal-500" />
    </div>
  );

  const contributions = data?.contributions || [];

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        label="STATES WORKSPACE"
        title="State SDG Contribution"
        subtitle={`${geo} · ${data?.total_projects || 0} projects · ${data?.total_sdgs_touched || 0} SDGs touched`}
        backUrl="/states/overview"
        backLabel="State Overview"
      />

      {contributions.length === 0 ? (
        <SectionCard>
          <div className="py-8 text-center">
            <p className="text-sm text-slate-500">No projects found for {geo}</p>
          </div>
        </SectionCard>
      ) : (
        <div className="space-y-3">
          {contributions.map((c) => (
            <div key={c.sdg} className="bg-white border border-slate-200 rounded-xl p-5 flex items-center gap-4">
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center text-white font-bold text-xl shrink-0"
                style={{ backgroundColor: SDG_PALETTE[c.sdg] || "#666" }}
              >
                {c.sdg}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900">{c.label}</p>
                <p className="text-xs text-slate-400 mt-0.5">Target {c.target}</p>
                <p className="text-xs text-slate-500 mt-0.5">{c.project_count} project(s)</p>
              </div>
              <div className="text-right flex-shrink-0">
                <MetricCard label="tCO₂e/yr" value={(c.total_reduction_tco2e || 0).toFixed(0)} color="text-teal-700" className="min-w-[90px]" />
              </div>
            </div>
          ))}
          {data?.note && (
            <p className="text-xs text-slate-400 italic text-center">{data.note}</p>
          )}
        </div>
      )}
    </div>
  );
}
